import {
  JobCategories,
  JobsSummaryOverviewDataPerWeek,
} from "@dwo/shared/dist/models/jobSummaryModel";
import { Box, useTheme } from "@material-ui/core";
import Chart, { ChartTooltipModel } from "chart.js";
import {
  selectSummaryCategoriesAndColors,
  selectSummarySelectedCategory,
  selectSummarySelectedDateRange,
} from "features/summary/summarySlice";
import React, { useEffect, useRef } from "react";
import { renderToString } from "react-dom/server";
import { useSelector } from "react-redux";
import { getEmptyDataset, getWeeklyBarChartData } from "utils/summaryUtils";
import { BarChartTooltip } from "./BarChartTooltip";

interface OverviewPerWeekChartProps {
  data: JobsSummaryOverviewDataPerWeek[];
}

const tooltipId = "overviewPerWeek-detailed-tooltip";
const totalDataLabel = "Total Week Hours / Costs";

export function OverviewPerWeekChart({ data }: OverviewPerWeekChartProps) {
  const theme = useTheme();
  const chartCanvasRef = useRef({} as HTMLCanvasElement);
  const categoriesAndColors = useSelector(selectSummaryCategoriesAndColors);
  const selectedCategory = useSelector(selectSummarySelectedCategory);
  const selectedDateRange = useSelector(selectSummarySelectedDateRange);

  useEffect(() => {
    if (!selectedDateRange) return;

    const {
      categoriesDatasetsMap,
      dateLabels,
      totalHours,
      totalWorkHoursArray,
      weeksMap,
    } = getWeeklyBarChartData(data, selectedDateRange);

    const totalHoursDataset = totalWorkHoursArray.map((weekHours: number) => {
      if (totalHours === 0) {
        return 1;
      }

      return totalHours - weekHours;
    });
    const datasets = categoriesAndColors.map((cac) => {
      const currentDataset = categoriesDatasetsMap.get(
        cac.label as JobCategories,
      );

      return {
        backgroundColor: cac.color,
        barThickness: 24,
        data: currentDataset || getEmptyDataset(dateLabels.length),
        hoverBackgroundColor: cac.color,
        label: cac.label,
      };
    });

    datasets.push({
      backgroundColor: theme.palette.grey[300],
      barThickness: 24,
      data: totalHoursDataset,
      hoverBackgroundColor: theme.palette.grey[300],
      label: totalDataLabel,
    });

    const barChart = new Chart(chartCanvasRef.current, {
      data: {
        datasets,
        labels: dateLabels,
      },
      options: {
        legend: {
          display: false,
        },
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {
              stacked: true,
              gridLines: {
                display: false,
              },
              ticks: {
                display: true,
                callback: (
                  _value: number | string,
                  index: number,
                  _values: number[] | string[],
                ) => {
                  return `W${index + 1}`;
                },
                fontColor: theme.palette.primary.dark,
                fontFamily: "Noto Sans",
                fontSize: 12,
                fontStyle: "bold",
              },
            },
          ],
          yAxes: [
            {
              stacked: true,
              gridLines: {
                display: false,
              },
              scaleLabel: {
                display: true,
                labelString: "Hours / Costs",
                fontColor: theme.palette.primary.dark,
                fontFamily: "Noto Sans",
                fontSize: 12,
                fontStyle: "bold",
              },
              ticks: {
                beginAtZero: true,
                display: false,
              },
            },
          ],
        },
        tooltips: {
          custom: (tooltipModel: ChartTooltipModel) => {
            let tooltipEl = document.getElementById(tooltipId);

            if (!tooltipEl) {
              tooltipEl = document.createElement("div");
              tooltipEl.id = tooltipId;
              document.body.appendChild(tooltipEl);
            }

            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = "0";
              return;
            }

            const barCategory =
              tooltipModel.body &&
              tooltipModel.body[0] &&
              tooltipModel.body[0].lines[0]
                ? tooltipModel.body[0].lines[0].split(":")[0]
                : undefined;
            const barColor =
              tooltipModel.labelColors && tooltipModel.labelColors[0]
                ? (tooltipModel.labelColors[0] as any).backgroundColor
                : undefined;
            const barDate = tooltipModel.title
              ? tooltipModel.title[0]
              : undefined;
            const barIndex =
              tooltipModel.dataPoints && tooltipModel.dataPoints[0]
                ? tooltipModel.dataPoints[0].index
                : undefined;

            // Safe guard. This values appear to be valid once tooltipModel.opacity > 0
            // Comparing barIndex to undefined 'cuz 0 is a valid value for the chart
            // Using !barIndex evaluates a truthy value 'cuz !0
            if (
              !barCategory ||
              !barColor ||
              !barDate ||
              barIndex === undefined
            ) {
              tooltipEl.style.opacity = "0";
              return;
            }

            const weekData = weeksMap.get(barDate);

            if (!weekData) {
              tooltipEl.style.opacity = "0";
              return;
            }

            let customTooltipHTMLString = undefined;

            if (barCategory === totalDataLabel) {
              customTooltipHTMLString = renderToString(
                <BarChartTooltip
                  cost={weekData.weekCosts.toFixed(2)}
                  date={`Week ${barIndex + 1}`}
                  hours={weekData.weekHours.toFixed(2)}
                  tooltipOption={{
                    color: theme.palette.grey[500],
                    label: "Week Costs / Hours",
                  }}
                />,
              );
            } else {
              const categoryData = weekData.weekCategoriesMap.get(
                barCategory as JobCategories,
              );

              if (!categoryData) {
                tooltipEl.style.opacity = "0";
                return;
              }

              customTooltipHTMLString = renderToString(
                <BarChartTooltip
                  cost={parseFloat(categoryData.cost).toFixed(2)}
                  date={`Week ${barIndex + 1}`}
                  hours={parseFloat(categoryData.hours).toFixed(2)}
                  tooltipOption={{
                    color: barColor,
                    label: barCategory,
                  }}
                />,
              );
            }

            const position = chartCanvasRef.current.getBoundingClientRect();

            tooltipEl.innerHTML = customTooltipHTMLString;
            tooltipEl.style.opacity = "1";
            tooltipEl.style.position = "absolute";
            tooltipEl.style.left = `${
              position.left + window.pageXOffset + tooltipModel.caretX
            }px`;
            tooltipEl.style.top = `${
              position.top + window.pageYOffset + tooltipModel.caretY
            }px`;
            tooltipEl.style.pointerEvents = "none";
          },
          enabled: false,
        },
      },
      type: "bar",
    });

    return () => {
      barChart.destroy();
    };
  }, [
    categoriesAndColors,
    data,
    selectedCategory,
    selectedDateRange,
    theme.palette,
  ]);

  return (
    <Box height="200px" maxWidth="360px" width="100%">
      <canvas ref={chartCanvasRef} />
      <div id={tooltipId} />
    </Box>
  );
}
